/**
 * BroadcastStatusIpcSyncManager
 *
 * Bridges SessionCoordinator's broadcast status → Playjector IPC.
 *
 * Playjector treats `hubBroadcastStatusMSG` as a trigger to run its global
 * per-device input flush (FlushKeyboard, FlushMouse, FlushTouches,
 * FlushGamepads) whenever the host transitions out of streaming. In the
 * legacy flow, apps/host/host-frontend/src/api/modules/events/playjectorHostModeEvents.ts
 * was the only sender of this message, and it is gated off when the new
 * coordinator flow (USE_MESSAGE_ROUTER) is active. Without this bridge,
 * Playjector never sees the stream-stop transition and stuck inputs linger.
 *
 * Single responsibility: listen for SessionCoordinator broadcast status
 * transitions and relay them over IPC. No state tracking here;
 * SessionCoordinator already guards against duplicate emits.
 */

import { generators } from '@playcastdotio/Messaging';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { SessionCoordinator } from '../SessionCoordinator';

export type BroadcastStatusIpcSyncManagerDeps = {
  ipcService: IIpcService;
  sessionCoordinator: SessionCoordinator;
  getHostAuthId: () => string;
};

export class BroadcastStatusIpcSyncManager {
  private unsubscribe: (() => void) | null = null;

  constructor(private readonly deps: BroadcastStatusIpcSyncManagerDeps) {}

  public start(): void {
    if (this.unsubscribe) {
      return;
    }
    this.unsubscribe = this.deps.sessionCoordinator.events.broadcastStatusChanged.addListener(
      ({ status }) => {
        const hostAuthId = this.deps.getHostAuthId();
        console.log(`[BroadcastStatusIpcSync] Forwarding broadcast status to Playjector: ${status}`);
        this.deps.ipcService.send(generators.hub.broadcastStatus(status, hostAuthId));
      },
    );
  }

  public stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }
}
